// frontend/src/components/conversation/chatdag/edges/ReconsiderEdge.tsx
/**
 * ReconsiderEdge (C2.3 Switchback) — the "reach back" edge from a reconsider
 * ORIGIN into the switchback merge_node. Deliberately distinct from the C2.2
 * forward MergeEdge: DASHED, a WARMER amber (orange-leaning), and a smooth-step
 * (elbow) route — so "reaches back to reconsider" reads at a glance, not just by
 * the dash (spec §0.1 B2 / ux). Per plan §0.0 D-T5 the mid-edge ↻ marker is
 * DROPPED: the dash + hue + the origin's ⤺ retrace marker already carry it, and
 * that keeps ↻ unambiguously the *button*. A hover word (no ↻ glyph) mirrors
 * MergeEdge's "folded back". NOT named 'switchback' — that edge concept is taken
 * by DagResponse.switchbacks (violet artifact edges, types/dag.ts).
 */
import React, { useState } from 'react';
import { EdgeProps, getSmoothStepPath } from 'reactflow';
import { useTheme } from '../../../../contexts/ThemeContext';

export interface ReconsiderEdgeData {
  kind: 'reconsider';
}

export const ReconsiderEdge: React.FC<EdgeProps<ReconsiderEdgeData>> = ({
  id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition,
}) => {
  const { darkMode } = useTheme();
  // Warmer than the C2.2 merge amber (#fbbf24/#d97706) — orange-leaning so the two
  // ambers stay distinct in both light + dark.
  const stroke = darkMode ? '#fb923c' : '#ea580c';
  const [edgePath] = getSmoothStepPath({
    sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, borderRadius: 12,
  });
  const [hovered, setHovered] = useState(false);
  return (
    <>
      <path
        id={id}
        className="react-flow__edge-path"
        d={edgePath}
        stroke={stroke}
        strokeWidth={2}
        strokeDasharray="5 5"
        fill="none"
        opacity={hovered ? 0.95 : 0.8}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{ cursor: 'default' }}
      />
      {hovered && (
        <text style={{ pointerEvents: 'none' }}>
          <textPath href={`#${id}`} startOffset="50%" textAnchor="middle" fontSize="9" fill={stroke}>
            reconsidered
          </textPath>
        </text>
      )}
    </>
  );
};
